import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import L from "leaflet";
import { Phone } from "lucide-react";
import { API } from "@/lib/api";
import { BrandMark, DEFAULT_BRAND } from "@/context/BrandContext";
import { fmtKm, sinceLabel } from "@/lib/fleet";

const DARK_TILES = "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png";
const POLL_MS = 20000;

const STATUS_META = {
  on_trip: { label: "ON TRIP", color: "#00FF66" },
  parked: { label: "PARKED", color: "#FF3B30" },
  idle: { label: "IDLE", color: "#A1A1AA" },
  maintenance: { label: "MAINTENANCE", color: "#FFCC00" },
  inactive: { label: "INACTIVE", color: "#52525B" },
};

const meta = (status) => STATUS_META[status] || STATUS_META.idle;

const Stat = ({ label, value, unit, accent, testid }) => (
  <div className="border border-white/10 bg-[#121212] px-3 py-3 sm:px-4 sm:py-4" data-testid={testid}>
    <p className="overline">{label}</p>
    <p
      className="font-head mt-1 text-2xl font-black tracking-tight sm:text-3xl"
      style={accent ? { color: accent } : {}}
    >
      {value}
      {unit && <span className="ml-1 text-[10px] font-normal text-[#52525B]">{unit}</span>}
    </p>
  </div>
);

const markerHtml = (label, status) => {
  const m = meta(status);
  const moving = status === "on_trip";
  return `<div class="doot-marker ${moving ? "moving" : "parked"}">${
    moving ? '<div class="ring"></div>' : ""
  }<div class="core" style="background:${m.color}"></div><div class="plate">${label}</div></div>`;
};

export default function Track() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const holder = useRef(null);
  const mapRef = useRef(null);
  const markers = useRef({});
  const fitted = useRef(false);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/public/track/${token}`);
      setData(res.data);
      setError("");
    } catch (err) {
      setError(err.response?.data?.detail || "This tracking link is not available");
    }
  }, [token]);

  useEffect(() => {
    load();
    const t = setInterval(load, POLL_MS);
    return () => clearInterval(t);
  }, [load]);

  const isFleet = data?.scope === "fleet";
  const units = isFleet ? data.vehicles : data ? [data] : [];

  useEffect(() => {
    if (!data || mapRef.current) return;
    const first = units.find((u) => u.current_location);
    if (!first) return;
    const map = L.map(holder.current, {
      center: [first.current_location.lat, first.current_location.lng],
      zoom: 8,
    });
    L.tileLayer(DARK_TILES, { maxZoom: 19, attribution: "© OpenStreetMap, © CARTO" }).addTo(map);
    mapRef.current = map;
    setTimeout(() => map.invalidateSize(), 200);
    const onResize = () => map.invalidateSize();
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
    };
  }, [data, units]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || units.length === 0) return;
    const points = [];
    units.forEach((u) => {
      if (!u.current_location) return;
      const pos = [u.current_location.lat, u.current_location.lng];
      points.push(pos);
      const icon = L.divIcon({
        className: "",
        html: markerHtml(u.plate_number, u.vehicle_status),
        iconSize: [34, 34],
        iconAnchor: [17, 17],
      });
      const existing = markers.current[u.plate_number];
      if (existing) {
        existing.setLatLng(pos);
        existing.setIcon(icon);
      } else {
        markers.current[u.plate_number] = L.marker(pos, { icon }).addTo(map);
      }
      if (!isFleet && u.destination_pos && !markers.current.__dest) {
        markers.current.__dest = L.marker([u.destination_pos.lat, u.destination_pos.lng], {
          icon: L.divIcon({
            className: "",
            html: '<div class="doot-marker idle"><div class="core" style="background:#00F0FF"></div><div class="plate">DESTINATION</div></div>',
            iconSize: [34, 34],
            iconAnchor: [17, 17],
          }),
        }).addTo(map);
        points.push([u.destination_pos.lat, u.destination_pos.lng]);
      }
    });
    if (!fitted.current && points.length > 0) {
      map.fitBounds(L.latLngBounds(points), { padding: [70, 70], maxZoom: 10 });
      fitted.current = true;
    }
  }, [units, isFleet]);

  if (error)
    return <div className="min-h-screen bg-[#0A0A0A]" data-testid="track-blank" />;

  if (!data)
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0A0A0A] text-xs tracking-[0.3em] text-[#52525B]">
        LOADING…
      </div>
    );

  const single = isFleet ? null : data;
  const brand = { ...DEFAULT_BRAND, ...(data.branding || {}) };
  const pct = single?.planned_distance_km
    ? Math.max(0, Math.min(100, (single.distance_covered_km / single.planned_distance_km) * 100))
    : 0;

  return (
    <div className="flex min-h-screen flex-col bg-[#0A0A0A]" data-testid="track-page">
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-white/10 px-4 py-3 sm:px-8">
        <div className="flex items-center gap-3">
          <BrandMark brand={brand} size={20} />
          <span className="overline mt-1">{isFleet ? "live fleet tracking" : "live shipment tracking"}</span>
        </div>
        <div className="flex items-center gap-2 border border-white/10 px-3 py-1.5 text-[11px]" data-testid="track-supervisor">
          <Phone size={11} className="text-[#00F0FF]" />
          {data.supervisor_phone || "contact dispatch"}
          {data.supervisor_name && <span className="text-[#52525B]">· {data.supervisor_name}</span>}
        </div>
      </header>

      <div className="grid flex-1 md:grid-cols-[1fr_320px] lg:grid-cols-[1fr_400px]">
        <div
          ref={holder}
          data-testid="track-map"
          className="h-[46vh] min-h-[280px] w-full sm:h-[52vh] md:h-auto md:min-h-0"
        />

        <aside className="doot-scroll border-t border-white/10 px-4 py-5 sm:px-5 sm:py-6 md:max-h-[calc(100vh-57px)] md:overflow-y-auto md:border-l md:border-t-0">
          <p className="overline">{data.label || (isFleet ? "shared fleet" : "shipment")}</p>

          {isFleet ? (
            <>
              <h1 className="font-head text-2xl font-black tracking-tight sm:text-3xl" data-testid="track-fleet-title">
                {units.length} TRUCKS
              </h1>
              <div className="mt-5 grid gap-3 sm:grid-cols-2 md:grid-cols-1" data-testid="track-fleet-list">
                {units.map((u) => {
                  const m = meta(u.vehicle_status);
                  const p = u.planned_distance_km
                    ? Math.max(0, Math.min(100, (u.distance_covered_km / u.planned_distance_km) * 100))
                    : 0;
                  return (
                    <div
                      key={u.plate_number}
                      data-testid={`track-fleet-item-${u.plate_number}`}
                      className="border border-white/10 bg-[#121212] px-3 py-3 sm:px-4 sm:py-4"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-head text-sm font-bold tracking-tight">{u.plate_number}</span>
                        <span
                          className="border px-1.5 py-0.5 text-[9px] tracking-[0.15em]"
                          style={{ color: m.color, borderColor: `${m.color}55` }}
                        >
                          {m.label}
                        </span>
                      </div>
                      <div className="mt-2 space-y-1 text-[11px]" data-testid={`track-legs-${u.plate_number}`}>
                        <p className="text-[#A1A1AA]">
                          <span className="text-[10px] tracking-[0.15em] text-[#52525B]">FROM </span>
                          {u.origin_name || "--"}
                        </p>
                        <p className="text-[#A1A1AA]">
                          <span className="text-[10px] tracking-[0.15em] text-[#52525B]">NOW </span>
                          <span className="text-[#00F0FF]">
                            {u.current_location_name ||
                              (u.current_location
                                ? `${u.current_location.lat.toFixed(4)}, ${u.current_location.lng.toFixed(4)}`
                                : "awaiting first fix")}
                          </span>
                        </p>
                        <p className="text-[#A1A1AA]">
                          <span className="text-[10px] tracking-[0.15em] text-[#52525B]">TO </span>
                          {u.destination_name || "No trip assigned"}
                        </p>
                      </div>
                      {u.planned_distance_km ? (
                        <>
                          <div className="mt-3 h-[2px] w-full bg-white/10">
                            <div className="h-full bg-[#00FF66]" style={{ width: `${p}%` }} />
                          </div>
                          <div className="mt-2 flex justify-between text-[10px] text-[#52525B]">
                            <span>{fmtKm(u.distance_remaining_km)} KM LEFT</span>
                            <span>{fmtKm(u.distance_covered_km)} KM DONE</span>
                          </div>
                        </>
                      ) : null}
                      <p className="overline mt-2">
                        driver {u.driver_name || "assigned"} · {sinceLabel(u.current_location_updated_at)} ago
                      </p>
                    </div>
                  );
                })}
              </div>
            </>
          ) : (
            <>
              <h1 className="font-head text-2xl font-black tracking-tight sm:text-3xl" data-testid="track-plate">
                {single.plate_number}
              </h1>
              <p className="mt-3 text-sm" data-testid="track-route">
                {single.origin_name} <span className="text-[#00F0FF]">→</span> {single.destination_name}
              </p>
              <p className="overline mt-2" data-testid="track-status">
                {meta(single.vehicle_status).label} · driver {single.driver_name || "assigned"}
              </p>
              {single.halt && null}

              <div className="mt-6">
                <div className="flex items-end justify-between">
                  <p className="overline">progress</p>
                  <p className="text-xs text-[#00FF66]" data-testid="track-progress">
                    {pct.toFixed(0)}%
                  </p>
                </div>
                <div className="mt-2 h-[2px] w-full bg-white/10">
                  <div className="h-full bg-[#00FF66] transition-[width] duration-500" style={{ width: `${pct}%` }} />
                </div>
              </div>

              <div className="mt-6 grid grid-cols-2 gap-3">
                <Stat label="distance left" value={fmtKm(single.distance_remaining_km)} unit="KM" accent="#00F0FF" testid="track-remaining" />
                <Stat label="covered" value={fmtKm(single.distance_covered_km)} unit="KM" testid="track-covered" />
                <Stat label="total" value={fmtKm(single.planned_distance_km)} unit="KM" testid="track-planned" />
              </div>

              <div className="mt-3 border border-white/10 bg-[#121212] px-4 py-4">
                <p className="overline">last known position</p>
                <p className="mt-1 text-sm" data-testid="track-location">
                  {single.current_location_name ||
                    (single.current_location
                      ? `${single.current_location.lat.toFixed(4)}, ${single.current_location.lng.toFixed(4)}`
                      : "awaiting first fix")}
                </p>
                <p className="overline mt-2">updated {sinceLabel(single.current_location_updated_at)} ago</p>
              </div>
            </>
          )}

          <p className="overline mt-6">
            this view refreshes automatically · driver contact details are private
          </p>
        </aside>
      </div>
    </div>
  );
}
